import React, { useState, useEffect, useCallback } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { ShoppingBag, Sparkles, TrendingUp, Activity, Heart, Flame, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { fetchPeptides } from "../api";
import { usePageSEO } from "../hooks/usePageSEO";
import { trackAffiliateClick, AFFILIATE_URLS } from "../config/affiliate";
import { getTierForPeptide, getSlugForPeptide } from "../data/peptideTiers";
import TierBadge from "../components/TierBadge";
import JsonLd from "../components/JsonLd";

const SITE_BASE = "https://www.peptidesformedspas.org";
const SITE_IMAGE = `${SITE_BASE}/og-image.jpg`;

const ShopPage = () => {
  usePageSEO();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  const loadProducts = useCallback(async () => {
    try {
      const data = await fetchPeptides();
      setProducts(data);
    } catch (error) {
      console.error("[ShopPage] Failed to load peptides:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // ItemList of Products — emitted only on /shop (the catalog). Each Product
  // includes the `image` field Google requires for merchant listings.
  const shopJsonLd = products.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${SITE_BASE}/shop#itemlist`,
    name: "PeptidesForMedSpas Research Catalog",
    numberOfItems: products.length,
    itemListElement: products.map((p, i) => {
      const slug = getSlugForPeptide(p);
      const url = slug ? `${SITE_BASE}/peptides/${slug}` : SITE_BASE;
      const priceMatch = String(p.price || "").match(/\d+(?:\.\d+)?/);
      return {
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Product",
          name: p.name,
          description: p.description,
          image: SITE_IMAGE,
          url,
          sku: `PFM-${p.id}`,
          category: p.category,
          brand: { "@type": "Brand", name: "PeptidesForMedSpas" },
          offers: {
            "@type": "Offer",
            url: p.buyLink || url,
            priceCurrency: "USD",
            price: priceMatch ? priceMatch[0] : "0",
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
          },
        },
      };
    }),
  } : null;

  const categories = [
    { id: "All", name: "All Products", icon: ShoppingBag, blurb: "Full curated catalog" },
    { id: "Anti-Aging & Skin", name: "Anti-Aging & Skin", icon: Sparkles, blurb: "GHK-Cu, Glutathione, NAD+" },
    { id: "Weight Loss & Metabolism", name: "Weight Loss & Metabolism", icon: TrendingUp, blurb: "5-Amino-1MQ" },
    { id: "Recovery & Repair", name: "Recovery & Repair", icon: Activity, blurb: "BPC-157, TB-500, PDA" },
    { id: "Hormone & Longevity", name: "Hormone & Longevity", icon: Heart, blurb: "Sermorelin, MOTS-c, α-Klotho mAb" },
    { id: "Sexual Wellness", name: "Sexual Wellness", icon: Flame, blurb: "PT-141 (Bremelanotide)" },
  ];

  const SLUG_MAP = {
    "1": "ghk-cu",
    "3": "nad",
    "4": "5-amino-1mq",
    "5": "bpc-157",
    "6": "tb-500",
    "8": "sermorelin",
    "9": "mots-c",
    "10": "pt-141",
    "11": "klotho"
  };

  const filteredProducts = activeCategory === "All"
    ? products
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      {shopJsonLd && <JsonLd id="shop-jsonld" data={shopJsonLd} />}

      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
              <ShoppingBag className="w-4 h-4 text-[#b89a6d]" />
              <span className="text-sm text-[#b89a6d] font-medium">Med Spa Peptide Catalog</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#3a2a26] mb-4">Shop Med Spa Research Peptides</h1>
            <p className="text-xl text-[#7a6b62] max-w-3xl mx-auto mb-4">
              A curated catalog of peptides studied in med spa, aesthetic, longevity, and wellness research — with transparent labeling.
            </p>
            <p className="text-sm text-[#a09186]">
              Products are research chemicals supplied for laboratory research only. Not medical advice.
            </p>
          </div>

          {/* Category Cards (top 5) */}
          <div id="categories" className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 mb-16">
            {categories.slice(1).map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`p-6 rounded-2xl border text-left transition-all ${
                  activeCategory === category.id
                    ? "bg-[#fdf2f4] border-[#d4a5ad]"
                    : "bg-white border-[#f0d8d4] hover:border-[#d4a5ad]"
                }`}
              >
                <div className="w-12 h-12 bg-[#fdf2f4] rounded-xl flex items-center justify-center mb-4">
                  <category.icon className="w-6 h-6 text-[#b89a6d]" />
                </div>
                <h3 className="text-base font-bold text-[#3a2a26] mb-2">{category.name}</h3>
                <p className="text-[#7a6b62] text-sm">{category.blurb}</p>
              </button>
            ))}
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === category.id
                    ? "bg-[#d4a5ad] text-white"
                    : "bg-white text-[#7a6b62] hover:bg-[#f7e8e4] border border-[#f0d8d4]"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#b89a6d] animate-spin" />
            </div>
          ) : (
            <div id="products" className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white border border-[#f0d8d4] rounded-2xl overflow-hidden hover:border-[#d4a5ad] transition-all group"
                >
                  <div className="p-6">
                    {/* Category Badge + Evidence Tier */}
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-3 py-1 bg-[#fdf2f4] text-[#b89a6d] text-xs font-medium rounded-full">
                          {product.category}
                        </span>
                        <TierBadge tier={getTierForPeptide(product).tier} testid={`shop-${product.id}`} />
                      </div>
                      {product.popular && (
                        <span className="px-2 py-1 bg-[#fdf2dc] text-[#b89a6d] text-xs font-medium rounded-full">Popular</span>
                      )}
                    </div>

                    {/* Product Name */}
                    <h3 className="text-xl font-bold text-[#3a2a26] mb-3 group-hover:text-[#b89a6d] transition-colors">
                      {product.name}
                    </h3>

                    {/* Product Info */}
                    <p className="text-sm text-[#7a6b62] line-clamp-3 mb-3">{product.description}</p>
                    <div className="space-y-1 mb-4">
                      <p className="text-xs text-[#a09186]">Form: Lyophilized</p>
                      <p className="text-xs text-[#a09186]">Research-grade specifications listed</p>
                    </div>

                    {/* Price & CTA */}
                    <div className="pt-4 border-t border-[#f0d8d4]">
                      <div className="text-2xl font-bold text-[#b89a6d] mb-4" data-testid={`price-${product.id}`}>
                        {product.price}
                      </div>
                      <Button
                        onClick={() => {
                          trackAffiliateClick({
                            partner: "paramount",
                            product: product.name,
                            destination: product.buyLink,
                            sourcePage: "/shop",
                          });
                          window.open(product.buyLink, "_blank");
                        }}
                        className="w-full bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold text-sm mb-3"
                        data-testid={`view-product-${product.id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Product
                      </Button>
                      {SLUG_MAP[product.id] && (
                        <Link
                          to={`/peptides/${SLUG_MAP[product.id]}`}
                          className="block text-center text-[#b89a6d] hover:text-[#a08560] text-sm font-medium transition-colors"
                          data-testid={`learn-more-${SLUG_MAP[product.id]}`}
                        >
                          Learn More About {product.name} &rarr;
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* View More CTA */}
          <div className="mt-12 text-center">
            <Button
              onClick={() => {
                trackAffiliateClick({
                  partner: "paramount",
                  product: "browse-full-catalog",
                  destination: AFFILIATE_URLS.paramount,
                  sourcePage: "/shop",
                });
                window.open(AFFILIATE_URLS.paramount, "_blank");
              }}
              className="bg-gradient-to-r from-[#d4a5ad] to-[#a08560] hover:from-[#c4949c] hover:to-[#a08560] text-[#3a2a26] font-bold px-12 py-6 text-lg"
            >
              Browse Supplier Catalog
              <ExternalLink className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Compliance Notice */}
          <div className="mt-16 p-8 bg-white border border-[#f0d8d4] rounded-2xl">
            <h3 className="text-lg font-bold text-[#3a2a26] mb-4 text-center">Product Information</h3>
            <div className="grid md:grid-cols-2 gap-6 text-sm text-[#7a6b62]">
              <div>
                <p className="font-medium text-[#5a4a40] mb-2">What each product page includes:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Peptide sequence / amino-acid info</li>
                  <li>Form & purity tier</li>
                  <li>Research notes & applicable warnings</li>
                  <li>Price range</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-[#5a4a40] mb-2">Our policies:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><Link to="/refund" className="text-[#b89a6d] hover:underline">Refund Policy</Link></li>
                  <li><Link to="/shipping" className="text-[#b89a6d] hover:underline">Shipping Policy</Link></li>
                  <li><Link to="/privacy" className="text-[#b89a6d] hover:underline">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="text-[#b89a6d] hover:underline">Terms of Service</Link></li>
                </ul>
              </div>
            </div>
            <p className="text-center text-[#a09186] text-xs mt-6">
              Products are research chemicals supplied for laboratory research only. Not intended to diagnose, treat, cure, or prevent any disease.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
